import {FC, FormEvent, useEffect, useState} from 'react'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {Form} from 'react-bootstrap'
import {useAuth} from '../../hooks/useAuth'
import {useTranslation} from 'react-i18next'
import {prefetchRoute} from '../../utils/routePrefetch'
import SocialAuthButtons from '../../components/auth/SocialAuthButtons'
import AuthEmailInput from '@components/ui/AuthEmailInput.tsx'
import AuthPasswordInput from '@components/ui/AuthPasswordInput.tsx'
import UnauthenticatedLayout from '../../components/layout/UnauthenticatedLayout'
import {authStyles} from '../../components/layout/authStyles'
import {useAuthForm} from '../../hooks/useAuthForm'
import {analyticsService} from '../../services/analyticsService'

const LoginPage: FC = () => {
    const [currentStep, setCurrentStep] = useState<'email' | 'password'>('email')
    const [loginError, setLoginError] = useState<string | null>(null)

    const {login, isAuthenticated} = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const {t} = useTranslation(['auth', 'common'])

    const {
        values,
        touched,
        errors,
        isLoading,
        handleFieldChange,
        handleFieldBlur,
        setLoading,
        isFormValid
    } = useAuthForm(false)

    const savedLocation = location.state?.from
    const from = savedLocation
        ? savedLocation.pathname + (savedLocation.search ?? '') + (savedLocation.hash ?? '')
        : '/dashboard'

    useEffect(() => {
        if (isAuthenticated) {
            navigate(from, {replace: true})
        } else {
            prefetchRoute('dashboard')
        }
    }, [isAuthenticated, navigate, from])

    const handleEmailSubmit = (e: FormEvent) => {
        e.preventDefault()
        if (values.email && !errors.email) {
            setCurrentStep('password')
        }
    }

    const handlePasswordSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault()
        setLoginError(null)
        setLoading(true)
        analyticsService.track('auth.login.attempted')

        const result = await login(values.email, values.password)

        if (result.success) {
            analyticsService.track('auth.login.success')
            navigate(from, {replace: true})
        } else if (result.error) {
            analyticsService.track('auth.login.failed', {error_type: result.error})
            setLoginError(result.error)
        }

        setLoading(false)
    }

    return (
        <UnauthenticatedLayout sideImage="/assets/login-image.jpg">
            <h1 className={authStyles.pageTitle}>{t('auth:sign_in')}</h1>

            {currentStep === 'email' ? (
                <Form onSubmit={handleEmailSubmit} data-testid="login-form">
                    <div>
                        <label className={authStyles.inputLabel}>
                            {t('common:fields.email_address')}
                        </label>
                        <AuthEmailInput
                            value={values.email}
                            onChange={handleFieldChange('email')}
                            onBlur={handleFieldBlur('email')}
                            placeholder={t('common:placeholders.enter_email')}
                            testId="email-input"
                            showSubmitButton
                            submitDisabled={!values.email || !!errors.email}
                            submitAriaLabel="Continue"
                            autoFocusOnDesktop
                        />
                        {touched.email && errors.email && (
                            <div className={authStyles.errorMessage}>{errors.email}</div>
                        )}
                    </div>
                </Form>
            ) : (
                <Form onSubmit={handlePasswordSubmit} data-testid="login-form">
                    <div className={authStyles.emailDisplay}>
                        <span>{values.email}</span>
                        <button
                            type="button"
                            onClick={() => setCurrentStep('email')}
                            className={authStyles.changeEmailButton}
                        >
                            {t('common:actions.edit')}
                        </button>
                    </div>
                    <div className={authStyles.formSection}>
                        <label className={authStyles.inputLabel}>
                            {t('common:fields.password')}
                        </label>
                        <AuthPasswordInput
                            value={values.password}
                            onChange={handleFieldChange('password')}
                            onBlur={handleFieldBlur('password')}
                            placeholder={t('common:placeholders.enter_password')}
                            testId="password-input"
                            autoComplete="current-password"
                            showSubmitButton
                            submitDisabled={!isFormValid() || isLoading}
                            isLoading={isLoading}
                            submitAriaLabel="Sign in"
                            autoFocusOnDesktop
                        />
                        {touched.password && errors.password && (
                            <div className={authStyles.errorMessage}>{errors.password}</div>
                        )}
                        {loginError && (
                            <div className={authStyles.errorMessage}>{loginError}</div>
                        )}
                    </div>

                    <div className={authStyles.forgotPasswordLink}>
                        <Link to="/forgot-password" className={authStyles.forgotPasswordLinkBold}>
                            {t('auth:forgot_password_question')}
                        </Link>
                    </div>

                    <Link
                        to="/forgot-password"
                        className={authStyles.resetPasswordButton}
                        data-testid="forgot-password-link"
                    >
                        {t('auth:reset_password_action')}
                    </Link>
                </Form>
            )}

            {currentStep === 'email' && (
                <>
                    <div className={authStyles.divider}>
                        <div className={authStyles.dividerLine}/>
                        <span className={authStyles.dividerText}>{t('auth:or_divider')}</span>
                        <div className={authStyles.dividerLine}/>
                    </div>

                    <SocialAuthButtons/>

                    <footer className={authStyles.footer}>
                        <span className={authStyles.footerText}>
                            {t('auth:no_account_question')}
                        </span>
                        <br/>
                        <Link to="/signup" className={authStyles.footerLink} data-testid="register-link">
                            {t('auth:register_here')}
                        </Link>
                    </footer>
                </>
            )}
        </UnauthenticatedLayout>
    )
}

export default LoginPage
